import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Banknote, CreditCard, Info, Pencil, Plus, Smartphone } from "lucide-react";
import { friendlyError } from "@/lib/errors";
import { useActiveOrg } from "@/features/tenancy/hooks";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { Modal } from "@/components/ui/Modal";
import { StatusPill } from "@/components/ui/StatusPill";
import { QueryBoundary } from "@/components/ui/QueryBoundary";
import { EmptyState } from "@/components/ui/states";
import { Field, Input, Textarea, Toggle } from "@/components/ui/Form";
import { toast } from "@/components/ui/toast";
import {
  SUGERIDOS,
  VACIO,
  aBorrador,
  createMethod,
  fetchMethods,
  toggleMethod,
  updateMethod,
  validar,
  type MethodDraft,
  type PaymentMethod,
} from "./api";

const ICONOS: Record<string, typeof CreditCard> = {
  yape: Smartphone,
  plin: Smartphone,
  cod: Banknote,
  cash: Banknote,
};

export function PaymentsPage() {
  const { org } = useActiveOrg();
  const qc = useQueryClient();
  const orgId = org?.organizationId ?? null;

  const [editando, setEditando] = useState<PaymentMethod | null>(null);
  const [creando, setCreando] = useState(false);

  const metodos = useQuery({
    queryKey: ["payment-methods", orgId],
    queryFn: () => fetchMethods(orgId!),
    enabled: Boolean(orgId),
  });

  const refrescar = () => qc.invalidateQueries({ queryKey: ["payment-methods", orgId] });

  async function cambiarEstado(m: PaymentMethod) {
    try {
      await toggleMethod(m.id, !m.active);
      void refrescar();
      toast.ok(m.active ? "Método desactivado." : "Método activado.");
    } catch (error) {
      toast.error(friendlyError(error));
    }
  }

  const lista = metodos.data ?? [];
  const activos = lista.filter((m) => m.active);

  return (
    <div className="flex flex-col gap-3">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-head font-semibold tracking-tight">Pagos</h1>
          <p className="mt-0.5 text-meta text-ink-2">
            Cómo puede pagar el comprador en tu checkout.
          </p>
        </div>
        <Button
          variant="primary"
          icon={<Plus className="size-3.5" />}
          onClick={() => setCreando(true)}
        >
          Nuevo método
        </Button>
      </header>

      {metodos.isSuccess && activos.length === 0 && lista.length > 0 && (
        <div className="rounded-lg border border-warn bg-warn-soft px-4 py-3 text-meta">
          Ningún método está activo: el comprador no verá cómo pagar.
        </div>
      )}

      <QueryBoundary
        query={metodos}
        isEmpty={(d) => d.length === 0}
        empty={
          <Panel>
            <EmptyState
              icon={<CreditCard className="size-5" />}
              title="Sin métodos de pago"
              description="Agrega al menos uno para que el checkout pueda cerrar una compra."
            />
          </Panel>
        }
      >
        {(filas) => (
          <div className="grid gap-2.5 sm:grid-cols-2">
            {filas.map((m) => {
              const Icono = ICONOS[m.code] ?? CreditCard;
              return (
                <Panel key={m.id}>
                  <div className="flex items-start gap-3">
                    <span className="grid size-9 shrink-0 place-items-center rounded-md bg-accent-soft text-accent">
                      <Icono className="size-4" strokeWidth={1.8} />
                    </span>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="truncate text-ui font-semibold">{m.name}</h3>
                        <code className="rounded bg-sunk px-1 font-mono text-mark text-ink-3">
                          {m.code}
                        </code>
                      </div>

                      {m.account_reference && (
                        <p className="mt-1 font-mono text-meta text-ink-2">
                          {m.account_reference}
                        </p>
                      )}
                      {m.instructions && (
                        <p className="mt-1 line-clamp-2 text-meta text-ink-3">
                          {m.instructions}
                        </p>
                      )}

                      <div className="mt-2.5 flex items-center gap-1.5">
                        <button type="button" onClick={() => void cambiarEstado(m)}>
                          <StatusPill tone={m.active ? "ok" : "neutral"}>
                            {m.active ? "Activo" : "Inactivo"}
                          </StatusPill>
                        </button>
                        {m.requires_proof && (
                          <StatusPill tone="warn">Verificación manual</StatusPill>
                        )}
                        {m.provider && <StatusPill tone="accent">{m.provider}</StatusPill>}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setEditando(m)}
                      className="rounded p-1.5 text-ink-2 hover:bg-sunk"
                      aria-label={`Editar ${m.name}`}
                    >
                      <Pencil className="size-3.5" />
                    </button>
                  </div>
                </Panel>
              );
            })}
          </div>
        )}
      </QueryBoundary>

      <div className="flex items-start gap-2.5 rounded-lg border border-line bg-sunk px-4 py-3">
        <Info className="mt-0.5 size-4 shrink-0 text-ink-3" />
        <p className="text-meta text-ink-2">
          Todos estos cobros se verifican a mano: entra el pedido, alguien confirma que
          el dinero llegó y lo registra en el pedido. Cuando se conecte una pasarela,
          esa confirmación pasará a ser automática y aparecerá aquí como método propio.
        </p>
      </div>

      {(creando || editando) && (
        <MethodModal
          metodo={editando}
          organizationId={orgId!}
          siguienteOrden={(lista.at(-1)?.sort_order ?? 0) + 10}
          usados={lista.map((m) => m.code)}
          onClose={() => {
            setCreando(false);
            setEditando(null);
          }}
          onSaved={() => {
            void refrescar();
            setCreando(false);
            setEditando(null);
          }}
        />
      )}
    </div>
  );
}

function MethodModal({
  metodo,
  organizationId,
  siguienteOrden,
  usados,
  onClose,
  onSaved,
}: {
  metodo: PaymentMethod | null;
  organizationId: string;
  siguienteOrden: number;
  usados: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [borrador, setBorrador] = useState<MethodDraft>(
    metodo ? aBorrador(metodo) : VACIO,
  );
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);

  const nuevo = !metodo;

  const set = <K extends keyof MethodDraft>(campo: K, valor: MethodDraft[K]) =>
    setBorrador((b) => ({ ...b, [campo]: valor }));

  const disponibles = SUGERIDOS.filter((s) => !usados.includes(s.code));

  async function guardar() {
    const fallos = validar(borrador, nuevo);
    if (nuevo && usados.includes(borrador.code.trim())) {
      fallos.code = "Ya tienes un método con ese código.";
    }
    setErrores(fallos);
    if (Object.keys(fallos).length > 0) return;

    setGuardando(true);
    try {
      if (metodo) await updateMethod(metodo.id, borrador);
      else await createMethod(organizationId, borrador, siguienteOrden);
      toast.ok(metodo ? "Método actualizado." : "Método agregado.");
      onSaved();
    } catch (error) {
      toast.error(friendlyError(error));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={metodo ? `Editar ${metodo.name}` : "Nuevo método de pago"}
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button size="sm" variant="primary" onClick={() => void guardar()} loading={guardando}>
            {metodo ? "Guardar" : "Agregar"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        {nuevo && disponibles.length > 0 && (
          <div>
            <p className="mb-1.5 text-meta font-medium">Los más usados</p>
            <div className="flex flex-wrap gap-1.5">
              {disponibles.map((s) => (
                <button
                  key={s.code}
                  type="button"
                  onClick={() =>
                    setBorrador((b) => ({
                      ...b,
                      code: s.code,
                      name: s.name,
                      requires_proof: s.requires_proof,
                    }))
                  }
                  className="rounded-md border border-line px-2.5 py-1 text-meta hover:bg-sunk"
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <Field
          label="Código"
          required
          hint={
            nuevo
              ? "Identificador interno. No se puede cambiar después."
              : "El código no se cambia: los pedidos ya hechos lo guardan."
          }
          error={errores.code}
        >
          <Input
            value={borrador.code}
            onChange={(e) => set("code", e.target.value.toLowerCase())}
            placeholder="yape"
            disabled={!nuevo}
            spellCheck={false}
          />
        </Field>

        <Field label="Nombre visible" required error={errores.name}>
          <Input
            value={borrador.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Yape"
          />
        </Field>

        <Toggle
          label="Necesita comprobante"
          hint="El comprador paga por fuera y alguien de la tienda verifica que llegó."
          checked={borrador.requires_proof}
          onChange={(v) => set("requires_proof", v)}
        />

        <Field
          label="Número o cuenta"
          hint="Se le muestra al comprador al confirmar el pedido."
          error={errores.account_reference}
        >
          <Input
            value={borrador.account_reference}
            onChange={(e) => set("account_reference", e.target.value)}
            placeholder="987 654 321"
          />
        </Field>

        <Field label="Instrucciones" hint="Una línea corta. Sale en el checkout.">
          <Textarea
            rows={2}
            value={borrador.instructions}
            onChange={(e) => set("instructions", e.target.value)}
            placeholder="Yapea y envíanos la captura por WhatsApp."
          />
        </Field>

        <Toggle
          label="Activo"
          hint="Si lo desactivas, deja de aparecer en el checkout."
          checked={borrador.active}
          onChange={(v) => set("active", v)}
        />
      </div>
    </Modal>
  );
}
